
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/config";
import { 
  BarChart3, 
  Users, 
  Shield, 
  MessageSquare, 
  Calendar, 
  CreditCard, 
  BookOpen, 
  GraduationCap, 
  Bell, 
  FileText, 
  LayoutDashboard, 
  Settings, 
  Cloud, 
  Lock, 
  Smartphone,
  Globe,
  Clock,
  CheckCircle,
  LucideIcon
} from "lucide-react";

// Map of available icons for the features section
export const iconMap: Record<string, LucideIcon> = {
  "BarChart3": BarChart3,
  "Users": Users,
  "Shield": Shield,
  "MessageSquare": MessageSquare,
  "Calendar": Calendar,
  "CreditCard": CreditCard,
  "BookOpen": BookOpen,
  "GraduationCap": GraduationCap,
  "Bell": Bell,
  "FileText": FileText,
  "LayoutDashboard": LayoutDashboard,
  "Settings": Settings,
  "Cloud": Cloud,
  "Lock": Lock,
  "Smartphone": Smartphone,
  "Globe": Globe,
  "Clock": Clock,
  "CheckCircle": CheckCircle
};

export interface FeatureItem {
  id: string;
  title: string;
  description: string;
  icon: string; // Key from iconMap
}

interface FeaturesConfig {
  heading: string;
  subheading: string;
  features: FeatureItem[];
}

const defaultFeatures: FeatureItem[] = [
  {
    id: "1",
    icon: "BarChart3",
    title: "Real-time Analytics",
    description: "Get instant insights into attendance, performance, and engagement with comprehensive dashboards."
  },
  {
    id: "2",
    icon: "Users",
    title: "Student Management",
    description: "Complete student lifecycle management from enrollment to graduation, all in one place."
  },
  {
    id: "3",
    icon: "Shield",
    title: "Secure & Compliant",
    description: "Enterprise-grade security with full FERPA compliance and data protection standards."
  },
  {
    id: "4",
    icon: "MessageSquare",
    title: "Communication Hub",
    description: "Real-time messaging between teachers, parents, and staff with instant notifications."
  },
  {
    id: "5",
    icon: "CreditCard",
    title: "Financial Tracking",
    description: "Manage fees, payments, and budgets with automated invoicing and reporting."
  },
  {
    id: "6",
    icon: "Users",
    title: "Staff Management",
    description: "Handle scheduling, attendance, and performance reviews for your entire team."
  },
];

const Features = () => {
  const [config, setConfig] = useState<FeaturesConfig>({
    heading: "Everything you need",
    subheading: "Powerful features designed to streamline every aspect of school administration.",
    features: defaultFeatures
  });

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const docRef = doc(db, "systemConfig/features");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<FeaturesConfig>;
          setConfig(prev => ({
            ...prev,
            ...data,
            features: data.features || prev.features
          }));
        }
      } catch (error) {
        console.error("Error fetching features:", error);
      }
    };

    fetchFeatures();
  }, []);

  return (
    <section id="features" className="py-24 lg:py-32 px-6 lg:px-8 relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="max-w-2xl mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, margin: "-50px" }}
              className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-slate-900 dark:text-slate-50"
            >
              {config.heading}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
              viewport={{ once: true, margin: "-50px" }}
              className="text-lg text-slate-600 dark:text-slate-400"
            >
              {config.subheading}
            </motion.p>
          </div>
          
          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {config.features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon] || BarChart3; // Fallback icon
              
              return (
                <motion.div
                    key={feature.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05, ease: "easeOut" }}
                    viewport={{ once: true, margin: "-50px" }}
                    className="group rounded-xl bg-slate-50 dark:bg-[#0f172a] border border-slate-200 dark:border-slate-800/60 p-6 hover:bg-white dark:hover:bg-[#1e293b] transition-all duration-300"
                    data-testid={`feature-card-${index}`}
                >
                    <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center mb-4 group-hover:border-blue-500/50 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 transition-colors">
                    <IconComponent className="w-5 h-5 text-slate-700 dark:text-blue-400" />
                    </div>
                    <h3 className="text-base font-bold mb-2 text-slate-900 dark:text-slate-50">{feature.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
  );
};

export default Features;
